import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./ScheduledTasksPage.scss";
import heroBanner from "../../assets/images/banner-scheduled-tasks.png";
import Copyright from "../../components/Copyright/Copyright";

const ScheduledTasksPage = () => {
    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="feature-page">
            {/* Hero Section */}
            <section className="feature-hero">
                <div className="container">
                    <div className="feature-hero-image">
                        <img src={heroBanner} alt="Scheduled Tasks" />
                    </div>
                </div>
            </section>

            {/* Scheduled Tasks Information */}
            <section className="feature-info">
                <div className="container">
                    <div className="feature-grid">
                        <div className="feature-details">
                            <h2>Scheduled tasks and automation</h2>
                            <p>
                                Automate your financial processes with CashManager's powerful scheduling system.
                                Set up recurring payments, automated reports, and routine maintenance tasks
                                to streamline your operations and reduce manual workload.
                            </p>

                            <div className="feature-methods">
                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Automatic bank statement import</h3>
                                        <p>
                                            Automate the import and allocation of bank statements from your bank accounts. Keep your financial records up to date.
                                        </p>
                                    </div>
                                </div>

                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Recurring payments</h3>
                                        <p>
                                            Automate regular payments such as salaries, rent, utilities, and supplier invoices.
                                            Set up flexible schedules with custom intervals and conditions.
                                        </p>
                                    </div>
                                </div>

                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Automated reporting</h3>
                                        <p>
                                            Generate and distribute financial reports automatically on schedule.
                                            Keep stakeholders informed with timely, accurate financial data.
                                        </p>
                                    </div>
                                </div>

                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Data synchronization</h3>
                                        <p>
                                            Keep your data synchronized across systems with automated import/export tasks.
                                            Ensure data consistency and reduce manual data entry errors.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="feature-features">
                            <h2>Key features</h2>
                            <div className="features-list">
                                <div className="feature-item">
                                    <h3>Flexible scheduling</h3>
                                    <p>Create custom schedules with daily, weekly or monthly intervals.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Error handling</h3>
                                    <p>Robust error handling with automatic retry mechanisms and detailed failure notifications.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Task monitoring</h3>
                                    <p>Real-time monitoring of task execution with status tracking.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Integration ready</h3>
                                    <p>Seamlessly integrate with external systems and APIs for comprehensive automation workflows.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Supported Task Types */}
            <section className="supported-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Automation capabilities</h2>
                        <p>Comprehensive task automation covering all aspects of your financial operations</p>
                    </div>

                    <div className="items-grid">
                        <div className="item">
                            <h3>Bank statement import</h3>
                            <ul>
                                <li>Automatic bank statement import</li>
                                <li>Automatic bank statement reconciliation</li>
                                <li>Direct statement import via secure channels</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>Payment automation</h3>
                            <ul>
                                <li>Recurring salary payments</li>
                                <li>Utility bill payments</li>
                                <li>Tax payments and filings</li>
                                <li>Loan and mortgage payments</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>Reporting automation</h3>
                            <ul>
                                <li>Daily financial summaries</li>
                                <li>Monthly management reports</li>
                                <li>Quarterly compliance reports</li>
                                <li>Custom dashboard updates</li>
                                <li>Audit trail generation</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>Integration automation</h3>
                            <ul>
                                <li>Integration with external systems</li>
                                <li>Syncronize data with your ERP or CRM</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>Supplier invoice processing</h3>
                            <ul>
                                <li>Automatic invoice processing</li>
                                <li>Text extraction (OCR) from PDF and image files</li>
                                <li>Electronic invoice processing</li>
                                <li>Invoice data validation</li>
                                <li>PEPPOL 3.0 compliance</li>
                                <li>Compliance with Latvian e-invoice (e-rēķini) regulations</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>System maintenance</h3>
                            <ul>
                                <li>Data cleanup</li>
                                <li>Data archiving</li>
                                <li>System resource optimization</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Implementation Process */}
            <section className="implementation-process">
                <div className="container">
                    <div className="section-header">
                        <h2>Implementation process</h2>
                        <p>Get started with task automation in just a few simple steps</p>
                    </div>

                    <div className="process-steps">
                        <div className="process-step">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Process analysis</h3>
                                <p>We analyze your current processes and identify automation opportunities.</p>
                            </div>
                        </div>

                        <div className="process-step">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Task configuration</h3>
                                <p>Set up and configure your automated tasks with appropriate schedules and conditions.</p>
                            </div>
                        </div>

                        <div className="process-step">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Testing & validation</h3>
                                <p>Comprehensive testing of all automated processes in a controlled environment.</p>
                            </div>
                        </div>

                        <div className="process-step">
                            <div className="step-number">4</div>
                            <div className="step-content">
                                <h3>Deployment & monitoring</h3>
                                <p>Deploy to production with ongoing monitoring and optimization support.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to automate your processes?</h2>
                        <p>
                            Streamline your financial operations with intelligent task automation.
                            Contact us today to discuss your automation needs.
                        </p>
                        <Link to="/onboarding" className="btn btn-secondary btn-large">Get started</Link>
                    </div>
                </div>
            </section>

            {/* Copyright Section */}
            <Copyright />
        </div>
    );
};

export default ScheduledTasksPage;
